<template>
  <transition name="fade">
    <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center modal-backdrop">
      <div class="glass-card rounded-lg p-6 max-w-md w-full mx-4">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-semibold text-white">QR Code Scanner</h3>
          <button @click="$emit('close')" class="text-gray-400 hover:text-white transition-colors">
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>
        
        <div class="scanner-frame mb-4">
          <div class="bg-gray-900 rounded-lg p-2">
            <div id="qr-reader" ref="qrReader" class="w-full max-w-sm"></div>
          </div>
        </div>
        
        <div class="flex justify-center space-x-4">
          <button 
            @click="startCameraScanner(true)"
            class="btn-secondary text-white px-6 py-2 rounded-lg"
          >
            <i class="fas fa-redo mr-2"></i>
            Scanner neu starten
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode'

export default {
  name: 'QRScanner',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    paused: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      html5QrCode: null,
      scanning: false,
      scanningPaused: false,
      errorMessage: ''
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.startCameraScanner()
        })
      } else {
        this.stopScanner()
      }
    }
  },
  methods: {
    async startCameraScanner(forceRestart = false) {
      this.errorMessage = ''

      try {
        // Check if QR reader element exists via ref
        const qrReaderElement = this.$refs.qrReader
        if (!qrReaderElement) {
          throw new Error('QR reader element not found in DOM')
        }


        if (forceRestart && this.scanning) {
          await this.stopScanner()
        } else if (this.scanning) {
          return
        }

        // Clear the QR reader element
        qrReaderElement.innerHTML = ''

        // Check if we're on iOS
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

        // Mobile-friendly camera configuration
        const cameraConfigs = isIOS ? [
          { facingMode: 'environment' },
          { facingMode: 'user' },
          undefined
        ] : [
          { facingMode: 'environment' },
          { facingMode: 'user' },
          { facingMode: { exact: 'environment' } },
          { facingMode: { exact: 'user' } },
          undefined
        ]

        if (!this.html5QrCode) {
          this.html5QrCode = new Html5Qrcode('qr-reader')
        }

        let cameraStarted = false
        let lastError = null

        for (const config of cameraConfigs) {
          try {
            // Mobile-friendly scanner config
            const scannerConfig = {
              fps: isIOS ? 5 : 10, // Lower FPS for mobile
              qrbox: isIOS ? { width: 250, height: 250 } : { width: 300, height: 300 }
            }

            // Start scanner directly
            await this.html5QrCode.start(
              config,
              scannerConfig,
              (decodedText) => {
                this.handleScanSuccess(decodedText)
              },
              (errorMessage) => {
                // Only log serious errors, not continuous scan errors
                if (errorMessage && !errorMessage.includes('No QR code found') && !errorMessage.includes('QR code not found')) {
                  console.log('Scan error:', errorMessage)
                }
              }
            )

            cameraStarted = true
            this.scanning = true
            break
          } catch (err) {
            console.log('Camera config failed:', config, err)
            lastError = err
            continue
          }
        }

        if (!cameraStarted) {
          throw lastError || new Error('No camera configuration worked')
        }

      } catch (err) {
        console.error('Camera initialization failed:', err)
        this.scanning = false
        this.errorMessage = 'Kamerafehler: ' + err.message
      }
    },

    async stopScanner() {
      if (this.html5QrCode && this.scanning) {
        try {
          await this.html5QrCode.stop()
          await this.html5QrCode.clear()
        } catch (err) {
          console.log('Scanner stop error:', err)
        } finally {
          this.scanning = false
        }
      }
    },

    handleScanSuccess(decodedText) {
      if (this.scanningPaused || this.paused) {
        return
      }

      this.scanningPaused = true
      this.$emit('scan-success', decodedText)

      setTimeout(() => {
        this.scanningPaused = false
      }, 1500)
    }
  },
  beforeUnmount() {
    this.stopScanner()
  },
  emits: ['close', 'scan-success']
}
</script>
